'use client';

import React from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Bug, Home, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

function buildReportHref(error?: Error): string {
  const q = new URLSearchParams({ type: 'bug' });
  if (typeof window !== 'undefined') {
    q.set('pageUrl', window.location.href);
  }
  if (error?.message) {
    q.set('title', `UI error: ${error.message}`.slice(0, 160));
  }
  // Correlation id from last API call if exposed on window (optional)
  const corr =
    typeof window !== 'undefined'
      ? (window as unknown as { __lastCorrelationId?: string }).__lastCorrelationId
      : undefined;
  if (corr) q.set('correlationId', corr);
  return `/feedback?${q.toString()}`;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
      }

      return <DefaultErrorFallback error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error?: Error;
  resetError: () => void;
}

function DefaultErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const { t } = useTranslation('common');
  const reportHref = buildReportHref(error);

  return (
    <div className="flex min-h-[400px] items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-xl">{t('errorBoundaryTitle')}</CardTitle>
          <CardDescription>{t('errorBoundaryDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm font-medium text-red-800">{t('errorBoundaryDetails')}</p>
              <p className="mt-1 text-sm text-red-700">{error.message}</p>
            </div>
          ) : null}

          <div className="flex flex-col space-y-2">
            <Button onClick={resetError} className="w-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('errorBoundaryTryAgain')}
            </Button>
            <Button variant="outline" className="w-full" asChild>
              <Link href={reportHref}>
                <Bug className="mr-2 h-4 w-4" />
                {t('errorBoundaryReport')}
              </Link>
            </Button>
            <Button variant="ghost" className="w-full" asChild>
              <Link href="/">
                <Home className="mr-2 h-4 w-4" />
                {t('errorBoundaryGoHome')}
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  return <DefaultErrorFallback error={error} resetError={resetError} />;
}

export function useErrorHandler() {
  return (error: Error, errorInfo?: { componentStack: string }) => {
    console.error('Error caught by useErrorHandler:', error, errorInfo);
  };
}
